import { useMemo } from "react";
import { Popover } from "antd5";

import Badge from "lib/core_components/Badge";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import { fileSearch02, infoCircle } from "lib/icons/untitled_ui/SVGs";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault, sysTextDefault } from "lib/themes/colors";
import { Flex } from "../../../styles/utility-components";

import css from "./DocumentSources.module.scss";

export const NOTICE_SOURCE = "STOTLES_NOTICE";

// Const used to denote that a source document was deleted
const DELETED_DOC_PLACEHOLDER = "DELETED_DOC_SOURCE";

export function isNoticeSource(
  source?: { documentId?: string | null },
  noticeId?: string,
): boolean {
  if (!source?.documentId) return false;
  if (source.documentId === NOTICE_SOURCE) return true;
  return !!noticeId && source.documentId === noticeId;
}

type Props = {
  noticeTitle: string | undefined;
  sources: BidReportEntry_Answer_Sources[];
  onSelectDocument: (source: BidReportEntry_Answer_Sources) => Promise<void>;
  noticeId?: string;
};

export default function DocumentSources({
  sources: sourcesWithDuplicates,
  onSelectDocument,
  noticeId,
  noticeTitle,
}: Props) {
  const sources = useMemo(
    () => removeDuplicatesAndAppendLocations(sourcesWithDuplicates),
    [sourcesWithDuplicates],
  );

  if (sources.length === 1) {
    const source = sources[0];
    // If the source has a single reference
    if (!source.locations || source.locations.length < 2) {
      return (
        <SourceLink
          source={sources[0]}
          count={1}
          onSelectDocument={onSelectDocument}
          noticeId={noticeId}
          noticeTitle={noticeTitle}
        />
      );
    }

    // If the source has multiple references, display the count similar to the popover
    // but open the document when clicked
    return (
      <div
        onClick={() => onSelectDocument(source)}
        className={css.sourceProperty}
      >{`${source.locations.length} references`}</div>
    );
  }

  return (
    <SourcesPopover
      sources={sources}
      onSelectDocument={onSelectDocument}
      noticeId={noticeId}
      noticeTitle={noticeTitle}
    />
  );
}

type SourceLinkProps = {
  noticeTitle: string | undefined;
  source: BidReportEntry_Answer_Sources;
  count: number;
  onSelectDocument: (source: BidReportEntry_Answer_Sources) => Promise<void>;
  noticeId?: string;
};
function SourceLink({ source, count, onSelectDocument, noticeId, noticeTitle }: SourceLinkProps) {
  if (source.documentId === DELETED_DOC_PLACEHOLDER) {
    return (
      <span className={css.linkContainer}>
        <UIcon svg={infoCircle} className={css.icon} size={15} color={sysTextDefault} />
        Source deleted
      </span>
    );
  }
  const linkText = (): string => {
    if (noticeTitle && isNoticeSource(source, noticeId)) {
      return noticeTitle;
    }

    // If document name is not provided and it's not a notice source
    // apryse will not find the document
    if (!source.fileName) {
      return "--";
    }

    return source.fileName;
  };
  return (
    <span className={css.linkContainer} onClick={() => onSelectDocument(source)}>
      <Flex alignItems="center" gap={8}>
        <UIcon svg={fileSearch02} className={css.icon} size={15} color={sysPrimaryDefault} />
        <EllipsisTooltipText
          fullText={linkText()}
          containerClassname={css.linkTextContainer}
          textProps={{
            className: css.linkText,
          }}
        />
      </Flex>
      {count > 1 && <Badge size={18}>{count}</Badge>}
    </span>
  );
}

function SourcesPopover({ sources, onSelectDocument, noticeId, noticeTitle }: Props) {
  const total = useMemo(
    () => sources.reduce((sum, { locations }) => sum + (locations?.length ?? 0), 0),
    [sources],
  );

  const content = (
    <div className={css.popoverContent}>
      {sources.map((source, index) => (
        <span key={index}>
          <SourceLink
            source={source}
            count={source.locations?.length ?? 0}
            onSelectDocument={onSelectDocument}
            noticeId={noticeId}
            noticeTitle={noticeTitle}
          />
        </span>
      ))}
    </div>
  );

  return (
    <Popover
      title={<h3 className={css.popoverTitle}>{`${total} references`}</h3>}
      content={content}
      arrow={false}
      trigger="hover"
      placement="bottom"
    >
      <span className={css.sourceProperty}>{`${total} references`}</span>
    </Popover>
  );
}

function removeDuplicatesAndAppendLocations(
  sources: BidReportEntry_Answer_Sources[],
): BidReportEntry_Answer_Sources[] {
  const combinedSources = sources.reduce(
    (map, source) => {
      if (source.documentId) {
        const current = map[source.documentId];
        if (current) {
          map[source.documentId] = {
            ...current,
            locations: [...(current.locations ?? []), ...(source.locations ?? [])],
          };
        } else {
          map[source.documentId] = source;
        }
      }
      return map;
    },
    {} as Record<string, BidReportEntry_Answer_Sources>,
  );
  return Object.values(combinedSources);
}
